/**
 * React Hook for Copilot Studio Integration
 * Manages bot communication, message history, and localStorage persistence
 *
 * Now supports on-demand authentication via loginFn parameter
 * Includes demo mode for local development with mock AI responses
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { CopilotAPI } from '../services/copilot-api';
import {
  getChatHistory,
  saveChatHistory,
  clearChatHistory,
  cleanupExpiredHistory,
  createMessage
} from '../utils/chatStorage';

// Check if running in demo/local mode
const isDemoMode = () => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

// Mock AI responses for demo mode
const DEMO_RESPONSES = {
  analysis: `## Ticket Summary

**Issue:** User cannot access email on their laptop

**Key Details:**
- Outlook showing "Disconnected" status
- Issue started after Windows update this morning
- Restart did not resolve the issue

**Initial Assessment:**
This appears to be a common post-Windows Update issue affecting Outlook connectivity. The "Disconnected" status typically indicates a problem with the Outlook profile, cached credentials, or network settings that may have been altered during the update.

**Affected User:** John Smith (Acme Corporation)`,

  rootCauses: `## Potential Root Causes

Based on the ticket details, here are the most likely causes:

1. **Corrupted Outlook Profile** - Windows updates can sometimes corrupt Outlook profiles, causing connectivity issues
2. **Credential Cache Issues** - The update may have cleared or corrupted cached credentials for the email server
3. **Network Configuration Changes** - Windows updates occasionally reset proxy settings or firewall rules
4. **Add-in Conflicts** - Post-update add-in compatibility issues can prevent Outlook from connecting
5. **Exchange/M365 Authentication** - Modern authentication tokens may need to be refreshed after system updates`,

  solutions: `## Recommended Solutions

Try these steps in order:

### Quick Fixes (Try First)
1. **Repair Office Installation**
   - Go to Control Panel > Programs > Microsoft 365
   - Click "Change" > "Quick Repair"
2. **Clear Credential Manager**
   - Open Credential Manager
   - Remove entries related to Outlook/Microsoft
3. **Test in Safe Mode**
   - Hold Ctrl while clicking Outlook to start in Safe Mode
   - If it works, an add-in is likely the cause

### If Quick Fixes Fail
4. **Create New Outlook Profile**
   - Control Panel > Mail > Show Profiles
   - Add new profile and set as default
5. **Check Proxy/Network Settings**
   - Verify proxy settings weren't changed
   - Test with VPN disconnected if applicable

### Escalation Path
If none of the above work, escalate to Level 2 for Exchange server-side investigation.`
};

// Simulate API delay
const simulateDelay = () => new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));

/**
 * Generate a unique conversation ID for a ticket session
 * Format: ticket-{ticketId}-{timestamp}-{random}
 * @param {number} ticketId - Ticket ID
 * @returns {string} Unique conversation ID
 */
function generateConversationId(ticketId) {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  return `ticket-${ticketId}-${timestamp}-${random}`;
}

/**
 * Check if a message references a different ticket
 * @param {string} message - User's message
 * @param {number|string} currentTicketId - Current ticket ID
 * @returns {Object} { isValid: boolean, referencedTicketId: string|null }
 */
function validateTicketReference(message, currentTicketId) {
  if (!message || !currentTicketId) {
    return { isValid: true, referencedTicketId: null };
  }

  const currentId = String(currentTicketId);

  // Patterns to detect ticket references:
  // - "ticket #123" or "ticket#123"
  // - "ticket 123"
  // - "#123" (standalone)
  // - "SR123" or "SR-123" or "SR 123" (service request format)
  // - "INC123" or "INC-123" (incident format)
  const ticketPatterns = [
    /ticket\s*#?\s*(\d+)/gi,
    /(?:^|\s)#(\d{4,})/gi,  // Standalone # followed by 4+ digits (to avoid markdown headers)
    /\bSR[-\s]?(\d+)/gi,
    /\bINC[-\s]?(\d+)/gi,
  ];

  for (const pattern of ticketPatterns) {
    let match;
    // Reset lastIndex for global patterns
    pattern.lastIndex = 0;

    while ((match = pattern.exec(message)) !== null) {
      const referencedId = match[1];

      // If the referenced ticket is different from current, it's invalid
      if (referencedId !== currentId) {
        return {
          isValid: false,
          referencedTicketId: referencedId
        };
      }
    }
  }

  return { isValid: true, referencedTicketId: null };
}

/**
 * Hook for Copilot integration with on-demand authentication
 * @param {string|null} accessToken - Current access token (may be null)
 * @param {string|number} ticketId - Current ticket ID
 * @param {Function} loginFn - Function to trigger authentication, returns Promise<token>
 * @param {Object} connectWiseUser - ConnectWise user info from getMemberAuthentication
 */
export function useCopilot(accessToken, ticketId, loginFn, connectWiseUser) {
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTicketId, setCurrentTicketId] = useState(null);

  // Ref to track if we've done initial load for this ticket
  const initialLoadDone = useRef(false);

  /**
   * Load conversation from localStorage on ticket change
   */
  useEffect(() => {
    if (!ticketId) return;

    // Clean up expired histories on first load
    cleanupExpiredHistory();

    // If ticket changed, load history for new ticket
    if (ticketId !== currentTicketId) {
      console.log('[useCopilot] Loading history for ticket:', ticketId);
      initialLoadDone.current = false;

      const stored = getChatHistory(ticketId);
      if (stored && stored.messages && stored.messages.length > 0) {
        console.log('[useCopilot] Restored', stored.messages.length, 'messages from localStorage');
        setMessages(stored.messages);
        setConversationId(stored.conversationId);
      } else {
        console.log('[useCopilot] No existing history for ticket');
        setMessages([]);
        setConversationId(null);
      }

      setCurrentTicketId(ticketId);
      setError(null);
      initialLoadDone.current = true;
    }
  }, [ticketId, currentTicketId]);

  /**
   * Save messages to localStorage whenever they change
   */
  useEffect(() => {
    // Only save after initial load and if we have a ticket ID
    if (!initialLoadDone.current || !currentTicketId) return;

    // Only save if we have messages and a conversation ID
    if (messages.length > 0 && conversationId) {
      saveChatHistory(currentTicketId, conversationId, messages);
    }
  }, [messages, conversationId, currentTicketId]);

  /**
   * Ensure we have a valid token, triggering login if needed
   * @returns {Promise<string>} The access token
   */
  const ensureAuthenticated = useCallback(async () => {
    // If we have a token, use it
    if (accessToken) {
      return accessToken;
    }

    // If we have a login function, try to authenticate
    if (loginFn) {
      console.log('[useCopilot] No token, triggering login...');
      try {
        const token = await loginFn();
        return token;
      } catch (err) {
        console.error('[useCopilot] Login failed:', err);
        throw new Error('Authentication required to analyze ticket');
      }
    }

    // No token and no way to get one
    throw new Error('Not authenticated - please refresh the page');
  }, [accessToken, loginFn]);

  /**
   * Analyze a ConnectWise ticket using Copilot
   * Triggers authentication if not already authenticated
   * @param {Object} ticketData - Full ticket data from ConnectWise
   */
  const analyzeTicket = useCallback(async (ticketData) => {
    if (!ticketData) {
      setError('No ticket data provided');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      // Ensure we have authentication (triggers login popup if needed)
      let token;
      try {
        token = await ensureAuthenticated();
      } catch (authErr) {
        setError(authErr.message);
        setLoading(false);
        return null;
      }

      console.log('[useCopilot] Starting ticket analysis for ID:', ticketData.id);

      // Determine conversation ID - reuse existing or generate new
      let currentConvId = conversationId;
      if (!currentConvId) {
        currentConvId = generateConversationId(ticketData.id);
        setConversationId(currentConvId);
        console.log('[useCopilot] Generated new conversation ID:', currentConvId);
      } else {
        console.log('[useCopilot] Reusing existing conversation ID:', currentConvId);
      }

      // Create the analysis request message
      const userMessage = `Please analyze ConnectWise ticket #${ticketData.id} and provide a brief summary of the issue`;

      // Add user message to history immediately (optimistic UI)
      const userMsg = createMessage('user', userMessage, 'analysis');
      setMessages(prev => [...prev, userMsg]);

      // Demo mode - return mock response
      if (isDemoMode()) {
        console.log('[useCopilot] DEMO MODE - returning mock analysis');
        await simulateDelay();
        const mockResponse = { message: DEMO_RESPONSES.analysis };
        const botMsg = createMessage('bot', mockResponse.message, 'analysis');
        setMessages(prev => [...prev, botMsg]);
        setLoading(false);
        return mockResponse;
      }

      // Create Copilot API client with access token and ConnectWise user info
      const api = new CopilotAPI(token, connectWiseUser);

      // Call the API (passing existing conversation ID)
      const response = await api.sendFollowUp(currentConvId, userMessage);

      console.log('[useCopilot] Analysis received:', response);

      // Add bot response to history
      const botMsg = createMessage('bot', response.message, 'analysis');
      setMessages(prev => [...prev, botMsg]);

      setLoading(false);
      return response;
    } catch (err) {
      console.error('[useCopilot] Error analyzing ticket:', err);
      setError(err.message);
      setLoading(false);
      return null;
    }
  }, [conversationId, ensureAuthenticated]);

  /**
   * Get mock response based on message content (for demo mode)
   */
  const getDemoResponse = (message) => {
    const lowerMessage = message.toLowerCase();
    if (lowerMessage.includes('root cause') || lowerMessage.includes('why') || lowerMessage.includes('cause')) {
      return DEMO_RESPONSES.rootCauses;
    }
    if (lowerMessage.includes('solution') || lowerMessage.includes('fix') || lowerMessage.includes('resolve') || lowerMessage.includes('recommend') || lowerMessage.includes('next step')) {
      return DEMO_RESPONSES.solutions;
    }
    // Generic follow-up response
    return `That's a great follow-up question about this ticket.

Based on my analysis of Ticket #${currentTicketId}:

The issue appears to be related to Outlook connectivity after a Windows update. The user has already tried restarting, which didn't help.

**Key considerations:**
- The timing (right after Windows update) is significant
- "Disconnected" status points to authentication or network issues
- The problem is persistent, not intermittent

Would you like me to elaborate on any specific aspect of this issue?`;
  };

  /**
   * Send a follow-up question about the current ticket
   * Triggers authentication if not already authenticated
   * @param {string} message - Follow-up question or request
   * @param {string} type - Message type: 'follow-up' or 'quick-action'
   */
  const sendFollowUp = useCallback(async (message, type = 'follow-up') => {
    if (!conversationId) {
      setError('No active conversation - please analyze the ticket first');
      return null;
    }

    // Validate that the message doesn't reference a different ticket
    const validation = validateTicketReference(message, currentTicketId);
    if (!validation.isValid) {
      setError(`This conversation is for Ticket #${currentTicketId}. To ask about Ticket #${validation.referencedTicketId}, please navigate to that ticket.`);
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      // Ensure we have authentication (triggers login popup if needed)
      let token;
      try {
        token = await ensureAuthenticated();
      } catch (authErr) {
        setError(authErr.message);
        setLoading(false);
        return null;
      }

      // Add user message to history immediately (optimistic UI)
      const userMsg = createMessage('user', message, type);
      setMessages(prev => [...prev, userMsg]);

      // Demo mode - return mock response
      if (isDemoMode()) {
        console.log('[useCopilot] DEMO MODE - returning mock follow-up response');
        await simulateDelay();
        const mockResponse = { message: getDemoResponse(message) };
        const botMsg = createMessage('bot', mockResponse.message, type);
        setMessages(prev => [...prev, botMsg]);
        setLoading(false);
        return mockResponse;
      }

      const api = new CopilotAPI(token, connectWiseUser);
      const response = await api.sendFollowUp(conversationId, message);

      // Add bot response to history
      const botMsg = createMessage('bot', response.message, type);
      setMessages(prev => [...prev, botMsg]);

      setLoading(false);
      return response;
    } catch (err) {
      console.error('[useCopilot] Error sending follow-up:', err);
      setError(err.message);
      setLoading(false);
      return null;
    }
  }, [conversationId, currentTicketId, ensureAuthenticated]);

  /**
   * Start a fresh conversation (clears history)
   */
  const startFreshConversation = useCallback(() => {
    if (currentTicketId) {
      clearChatHistory(currentTicketId);
      console.log('[useCopilot] Cleared history for ticket:', currentTicketId);
    }
    setMessages([]);
    setConversationId(null);
    setError(null);
  }, [currentTicketId]);

  /**
   * Clear current analysis (alias for backward compatibility)
   */
  const clearAnalysis = useCallback(() => {
    startFreshConversation();
  }, [startFreshConversation]);

  /**
   * Check if API is healthy
   */
  const checkHealth = useCallback(async () => {
    try {
      const token = await ensureAuthenticated();
      const api = new CopilotAPI(token);
      await api.checkHealth();
      return true;
    } catch (err) {
      console.error('[useCopilot] Health check failed:', err);
      return false;
    }
  }, [ensureAuthenticated]);

  return {
    // State
    messages,
    conversationId,
    loading,
    error,
    hasHistory: messages.length > 0,

    // Methods
    analyzeTicket,
    sendFollowUp,
    startFreshConversation,
    clearAnalysis,
    checkHealth
  };
}
